import React from 'react';
import SelectDropdown from '../../components/select';
import MultiSelectDropdown from '../../components/multi-select';
import { FormErrors } from './types';
import { RoleFormData, UserFormData } from '../../pages/user-form-types';
import { Local as LookupLocal, Province } from '../../services/lookups.service';

interface UserFormProps {
    formData: UserFormData;
    errors: FormErrors;
    roles: RoleFormData[];
    provinces: Province[];
    filteredLocals: LookupLocal[];
    showRegionLocal: boolean;
    isSubmitting: boolean;
    onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onRoleSelect: (value: string) => void;
    onRegionSelect: (selected: string[]) => void;
    onLocalSelect: (selected: string[]) => void;
    onSubmit: () => void;
}

const UserForm: React.FC<UserFormProps> = ({
    formData,
    errors,
    roles,
    provinces,
    filteredLocals,
    showRegionLocal,
    isSubmitting,
    onInputChange,
    onRoleSelect,
    onRegionSelect,
    onLocalSelect,
    onSubmit
}) => {
    const handleStatusToggle = () => {
        const newStatus = formData.status === 'active' ? 'inactive' : 'active';
        const event = {
            target: {
                name: 'status',
                value: newStatus
            }
        } as React.ChangeEvent<HTMLInputElement>;
        
        onInputChange(event);
    };

    return (
        <div className="max-h-[80vh] sm:max-h-[70vh] overflow-y-auto scrollbar-hide">
            <div className="mt-4 sm:mt-6">
                <div className="space-y-4">
                    <div className="relative pb-4">
                        <label className="block text-small font-medium text-text-secondary mb-1">
                            Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="input-name"
                            type="text"
                            name="name"
                            placeholder="Enter full name"
                            className={`w-full px-3 py-2 text-body border ${errors.name ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
                            value={formData.name}
                            onChange={onInputChange}
                        />
                        {errors.name && (
                            <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                        )}
                    </div>

                    <div className="relative pb-4">
                        <label className="block text-small font-medium text-text-secondary mb-1">
                            Email ID <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="input-email"
                            type="email"
                            name="email"
                            placeholder="Enter email id"
                            className={`w-full px-3 py-2 text-body border ${errors.email ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
                            value={formData.email}
                            onChange={onInputChange}
                        />
                        {errors.email && (
                           <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                        )}
                    </div>

                    <div className="relative pb-4">
                        <label className="block text-small font-medium text-text-secondary mb-1">
                            Mobile Number <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="input-mobile"
                            type="tel"
                            name="mobileNumber"
                            placeholder="Enter mobile number"
                            className={`w-full px-3 py-2 text-body border ${errors.mobileNumber ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
                            value={formData.mobileNumber}
                            onChange={onInputChange}
                        />
                        {errors.mobileNumber && (
                           <p className="mt-1 text-xs text-red-500">{errors.mobileNumber}</p>
                        )}
                    </div>

                    <div className="relative pb-4">
                        <label className="block text-small font-medium text-text-secondary mb-1">
                            Role <span className="text-red-500">*</span>
                        </label>
                        <SelectDropdown
                            testId="role-select"
                            options={roles
                                .filter(role => role.name !== '1')
                                .map(role => ({
                                    id: role.id,
                                    name: role.name === 'organizer' ? 'Organizer' :
                                        role.name === 'interRepre' ? 'International Representative' :
                                            role.name === 'admin' ? 'Admin' :
                                                role.name
                                }))}
                            onSelect={onRoleSelect}
                            getValue={(option) => option.id}
                            getDisplayText={(option) => option.name}
                            placeholder="Select a role"
                            selectedValue={formData.role}
                        />
                        {errors.role && (
                           <p className="mt-1 text-xs text-red-500">{errors.role}</p>
                        )}
                    </div>

                    {showRegionLocal && (
                        <>
                            <div className="relative pb-4">
                                <label className="block text-small font-medium text-text-secondary mb-1">
                                    Province <span className="text-red-500">*</span>
                                </label>
                                <MultiSelectDropdown
                                    testId="select-region"
                                    options={provinces.map(province => ({
                                        id: province.id,
                                        name: province.name
                                    }))}
                                    onSelect={onRegionSelect}
                                    getValue={(option) => option.id}
                                    getDisplayText={(option) => option.name}
                                    placeholder="Select a province"
                                    selectedValues={formData.region ?? []} 
                                    showSelectAll={true}
                
                                />
                                {errors.region && (
                                   <p className="mt-1 text-xs text-red-500">{errors.region}</p>
                                )}
                            </div>
                            {formData.region && filteredLocals.some(local => local.unionNumber && String(local.unionNumber).toUpperCase() !== 'N/A') ? (
                                <div className="relative pb-4">
                                    <label className="block text-small font-medium text-text-secondary mb-1">
                                        Local Areas <span className="text-red-500">*</span>
                                    </label>
                                    <MultiSelectDropdown
                                        testId="local-areas-select"
                                        options={filteredLocals
                                            .filter(local => local.unionNumber && String(local.unionNumber).toUpperCase() !== 'N/A')
                                            .map(local => ({
                                                id: local.id,
                                                name: `${local.code} - Local ${local.unionNumber}`
                                            }))}
                                        onSelect={onLocalSelect}
                                        getValue={(option) => option.id}
                                        getDisplayText={(option) => option.name}
                                        placeholder="Select local areas"
                                        selectedValues={formData.local ?? []}
                                    />
                                    {errors.local && (
                                       <p className="mt-1 text-xs text-red-500">{errors.local}</p>
                                    )}
                                </div>
                            ) : null}
                        </>
                    )}

                    <div className="relative flex items-center">
                        <label className="block text-small font-medium text-text-secondary mr-1">
                            Status
                        </label>
                        <div className="flex items-center">
                            <label className="relative flex items-center cursor-pointer">
                                <input
                                    id='user-activity-status'
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={formData.status === 'active'}
                                    onChange={handleStatusToggle}
                                />
                                <div className={`w-11 h-6 bg-text-secondary rounded-full peer-checked:bg-primary 
                                    after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                    after:bg-white after:border-DEFAULT after:border after:rounded-full 
                                    after:h-5 after:w-5 after:transition-all 
                                    peer-checked:after:translate-x-full peer-checked:after:border-white`}>
                                </div>
                            </label>
                            <span className="ml-2 text-text-primary">{formData.status.charAt(0).toUpperCase() + formData.status.slice(1)}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="sticky bottom-0 bg-white pt-8 mt-8 flex justify-between">
                <button
                    type="button"
                    id="btn-save-user"
                    onClick={onSubmit}
                    className="btn-primary z-9 w-full"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Creating User...' : 'Create User'}
                </button>
            </div>
        </div>
    );
};

export default UserForm;
